import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parseISO } from "date-fns";

// Custom component to display rich text content (read-only)
const RichTextDisplay = ({
  value,
  placeholder = "No content",
  height = "auto",
}) => {
  if (!value || value.trim() === "") {
    return (
      <Typography
        sx={{
          minHeight: height,
          padding: "12px",
          fontFamily: FONT_FAMILY,
          fontSize: "14px",
          color: COLORS.MUTED_TEXT,
          fontStyle: "italic",
          border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
          borderRadius: "0 0 8px 8px",
          backgroundColor: "#fff",
        }}
      >
        {placeholder}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
        borderRadius: "0 0 8px 8px",
        minHeight: height,
        padding: "12px",
        fontFamily: FONT_FAMILY,
        fontSize: "14px",
        color: COLORS.PRIMARY,
        backgroundColor: "#fff",
        "& p": {
          margin: "0 0 8px 0",
          color: COLORS.PRIMARY,
        },
        "& ul, & ol": {
          margin: "0 0 8px 0",
          paddingLeft: "20px",
          color: COLORS.PRIMARY,
        },
        "& li": {
          color: COLORS.PRIMARY,
        },
        "& strong": {
          fontWeight: 600,
        },
        "& em": {
          fontStyle: "italic",
        },
        "& u": {
          textDecoration: "underline",
        },
      }}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
};

// ================= FLEX HELPER COMPONENTS =================
const FlexRow = ({ children, header }) => (
  <Box
    display="flex"
    alignItems="stretch"
    sx={{
      py: header ? 1.2 : 1,
      fontWeight: header ? 600 : 400,
    }}
  >
    {children}
  </Box>
);

const FlexCell = ({
  children,
  flex,
  w,
  sx = {},
  borderBottom = "none",
  ...props
}) => (
  <Box
    flex={flex}
    width={w}
    px={1}
    sx={{
      ...TYPOGRAPHY.BODY_LARGE,
      borderBottom: borderBottom,
      wordWrap: "break-word",
      whiteSpace: "normal",
      overflowWrap: "break-word",
      lineHeight: 1.4,
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

const FlexRowInner = ({ children, header }) => (
  <Box
    display="flex"
    flex={1}
    alignItems="stretch"
    sx={{
      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
    }}
  >
    {children}
  </Box>
);

const FlexEnd = ({ children, mt }) => (
  <Box display="flex" justifyContent="flex-end" mt={mt}>
    {children}
  </Box>
);

export default function ViewWMR() {
  const { projectId: urlProjectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get data from navigation state (following ViewWPR pattern)
  const {
    weekType,
    weekStartDate: initialWeekStartDate,
    weekEndDate: initialWeekEndDate,
    projectName: propProjectName,
    cardData,
  } = location.state || {};

  // Week date state (following ViewWPR pattern) with validation
  const [weekStartDate, setWeekStartDate] = useState(() => {
    if (!initialWeekStartDate) return null;
    try {
      const date = new Date(initialWeekStartDate);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.warn("Invalid initialWeekStartDate:", initialWeekStartDate);
      return null;
    }
  });

  const [weekEndDate, setWeekEndDate] = useState(() => {
    if (!initialWeekEndDate) return null;
    try {
      const date = new Date(initialWeekEndDate);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.warn("Invalid initialWeekEndDate:", initialWeekEndDate);
      return null;
    }
  });

  // Get projectId from URL params first, then fallback to location state
  const projectId = urlProjectId || location.state?.projectId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // WMR data state
  const [wmrData, setWmrData] = useState({
    workExecuted: {
      rccWork: "",
      civilWorks: "",
      masonryFinishing: "",
      mepWorks: "",
      externalDevelopment: "",
    },
    workInProgress: {
      mergedProgress: [],
    },
    manpowerDeployment: {
      laborTypes: [],
      dates: [],
    },
    machineryEquipment: [],
    materialStatus: [],
    qualityControl: [],
    safetyHSE: {
      safetyCompliance: "",
      toolboxTalks: "",
      accidentsNearMisses: "",
    },
    issuesConstraints: {
      technical: "",
      material: "",
      manpower: "",
      designApproval: "",
    },
    instructionsDecisions: [],
    reports: [],
    decisionDiscussion: "",
    planNextWeek: {
      targetActivities: "",
      resourcePlanning: "",
    },
    photos: [],
  });

  // Week days data for Planned vs Actual section
  const [weekDaysData, setWeekDaysData] = useState([]);

  // Project Layout Image state
  const [projectLayoutImage, setProjectLayoutImage] = useState(null);
  const [projectLayoutImagePreview, setProjectLayoutImagePreview] =
    useState(null);

  // Loading states
  const [downloading, setDownloading] = useState(false);
  const [currentWMR, setCurrentWMR] = useState(null);

  // Format date functions with validation
  const formatDateForWeekHeader = (date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return ""; // Check if date is invalid
      return format(d, "dd/MM/yy");
    } catch (error) {
      console.warn("Invalid date in formatDateForWeekHeader:", date);
      return "";
    }
  };

  const formatDateForWeekColumn = (date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return ""; // Check if date is invalid
      const day = d.getDate();
      const suffix = getDaySuffix(day);
      const month = d.toLocaleString("default", { month: "short" });
      const year = d.getFullYear().toString().slice(-2);
      const dayName = d.toLocaleString("default", { weekday: "long" });

      return `${dayName} ${day}${suffix} ${month} '${year}`;
    } catch (error) {
      console.warn("Invalid date in formatDateForWeekColumn:", date);
      return "";
    }
  };

  // Safe date formatter helper
  const safeFormat = (date, formatStr, fallback = "") => {
    if (!date) return fallback;
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return fallback; // Check if date is invalid
      return format(d, formatStr);
    } catch (error) {
      console.warn("Invalid date in safeFormat:", date, error);
      return fallback;
    }
  };

  const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // Generate week days data for Planned vs Actual section
  const generateWeekDaysData = (startDate, endDate) => {
    if (!startDate || !endDate) return [];

    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const weekDataArray = [];

    const currentDate = new Date(startDate);

    for (let i = 0; i < 6; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);

      weekDataArray.push({
        day: days[i],
        date: date,
        dateStr: safeFormat(date, "yyyy-MM-dd"),
        formattedDate: formatDateForWeekColumn(date),
        entries: [],
      });
    }

    return weekDataArray;
  };

  // Fetch WMR data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Debug logging
        console.log("=== ViewWMR Diagnostic Log ===");
        console.log("projectId:", projectId);
        console.log("weekStartDate (raw):", weekStartDate);
        console.log("weekEndDate (raw):", weekEndDate);

        // Safely format dates for query to avoid TZ shifts
        const weekStartQuery = weekStartDate
          ? format(weekStartDate, "yyyy-MM-dd")
          : null;
        const weekEndQuery = weekEndDate
          ? format(weekEndDate, "yyyy-MM-dd")
          : null;
        console.log("Query Params:", {
          weekStartDate: weekStartQuery,
          weekEndDate: weekEndQuery,
        });

        // Validate projectId
        if (!projectId) {
          setError("Project ID is missing from URL");
          showToast("error", "Error", "Project ID is missing from URL");
          return;
        }

        // Validate week dates
        if (!weekStartDate || !weekEndDate) {
          setError(
            "Week dates are missing. Please navigate from the EditReport page.",
          );
          showToast(
            "error",
            "Error",
            "Week dates are missing. Please navigate from the EditReport page.",
          );
          return;
        }

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // Fetch WMR by project and week dates
        const response = await axios.get(
          `${url}/api/wmr/fetch-by-project/${projectId}?weekStartDate=${weekStartQuery}&weekEndDate=${weekEndQuery}`,
          config,
        );

        console.log("API response wmrs count:", response.data.wmrs?.length);

        if (response.data.wmrs && response.data.wmrs.length > 0) {
          // Get the first WMR that matches the week dates
          const wmr =
            response.data.wmrs.find((w) => {
              const wmrStartStr = format(
                new Date(w.weekStartDate),
                "yyyy-MM-dd",
              );
              const wmrEndStr = format(new Date(w.weekEndDate), "yyyy-MM-dd");
              return (
                wmrStartStr === weekStartQuery && wmrEndStr === weekEndQuery
              );
            }) || response.data.wmrs[0];

          console.log("Matched/Fallback WMR ID:", wmr._id);
          console.log(
            "WMR manpowerDeployment length:",
            wmr.manpowerDeployment?.length,
          );

          // Set current WMR for download functionality
          setCurrentWMR(wmr);

          // Fetch project details using the projectId from URL params
          const projectResponse = await axios.get(
            `${url}/api/inhouse-projects/fetch-by/${projectId}`,
            config,
          );

          if (projectResponse.data.project) {
            setProject(projectResponse.data.project);

            // Fetch companies for PMC dropdown
            const companiesResponse = await axios.get(
              `${url}/api/company/fetch`,
              config,
            );

            if (companiesResponse.data.data) {
              setCompanies(companiesResponse.data.data);
              if (companiesResponse.data.data.length > 0) {
                setSelectedCompany(companiesResponse.data.data[0]._id);
              }
            }
          }

          // Set week dates from the WMR (they're already set from location state)
          if (wmr.weekStartDate && wmr.weekEndDate) {
            const startDate = new Date(wmr.weekStartDate);
            const endDate = new Date(wmr.weekEndDate);

            // Initialize week data
            initializeWeekData(startDate, endDate);

            // Populate WMR data
            populateWMRData(wmr);
          }

          showToast("success", "Success", "WMR data loaded successfully!");
        } else {
          setError("No WMR found for the selected week.");
          showToast("error", "Error", "No WMR found for the selected week.");
        }
      } catch (err) {
        console.error("Error fetching WMR data:", err);
        setError("Failed to load WMR data. Please try again.");
        showToast("error", "Error", "Failed to load WMR data.");
      } finally {
        setLoading(false);
      }
    };

    if (projectId && token && weekStartDate && weekEndDate) {
      fetchData();
    }
  }, [projectId, token, weekStartDate, weekEndDate]);

  // Initialize week data
  const initializeWeekData = (startDateParam, endDate) => {
    // Generate dates for the week
    const dates = [];
    let startDate;
    if (typeof startDateParam === "string") {
      const [year, month, day] = startDateParam
        .split("T")[0]
        .split("-")
        .map(Number);
      startDate = new Date(year, month - 1, day);
    } else {
      startDate = new Date(startDateParam);
    }

    const currentDate = new Date(startDate);

    for (let i = 0; i < 6; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);
      // Format to just date without time for comparison
      dates.push(format(date, "yyyy-MM-dd"));
    }

    // Update manpower deployment dates (formatted differently)
    const displayDates = dates.map((date) =>
      format(new Date(date), "dd/MM/yy"),
    );

    setWmrData((prev) => ({
      ...prev,
      manpowerDeployment: {
        ...prev.manpowerDeployment,
        dates: displayDates,
      },
    }));

    // Initialize week days data for Planned vs Actual section
    const weekDays = generateWeekDaysData(startDate, endDate);
    setWeekDaysData(weekDays);
  };

  // Populate form data from existing WMR
  const populateWMRData = (wmr) => {
    console.log("=== populateWMRData Diagnostic ===");
    console.log(
      "WMR ManpowerDeployment raw:",
      JSON.stringify(wmr.manpowerDeployment, null, 2),
    );

    // Work Executed During Week
    if (wmr.workExtendedDuringWeek && wmr.workExtendedDuringWeek.length > 0) {
      const workExecuted = {
        rccWork: "",
        civilWorks: "",
        masonryFinishing: "",
        mepWorks: "",
        externalDevelopment: "",
      };

      wmr.workExtendedDuringWeek.forEach((work) => {
        if (work.workType === "RCC Works")
          workExecuted.rccWork = work.description || "";
        else if (work.workType === "Civil Works")
          workExecuted.civilWorks = work.description || "";
        else if (work.workType === "Masonry / Finishing Works")
          workExecuted.masonryFinishing = work.description || "";
        else if (work.workType === "MEP Works")
          workExecuted.mepWorks = work.description || "";
        else if (work.workType === "External Development")
          workExecuted.externalDevelopment = work.description || "";
      });
      setWmrData((prev) => ({ ...prev, workExecuted }));
    }

    // Work In Progress
    if (wmr.workInProgress && wmr.workInProgress.length > 0) {
      const mergedProgress = [];

      wmr.workInProgress.forEach((entry) => {
        mergedProgress.push({
          activity: entry.activity || "",
          location: entry.location || "",
          status: entry.status || "",
          remark: entry.remarks || "",
        });
      });

      setWmrData((prev) => ({
        ...prev,
        workInProgress: {
          mergedProgress: mergedProgress,
        },
      }));
    }

    // Planned vs Actual Progress
    if (wmr.plannedActualProgress && wmr.plannedActualProgress.length > 0) {
      console.log(
        "Populating planned vs actual data:",
        wmr.plannedActualProgress,
      );

      // Create a fresh copy of weekDaysData
      const updatedWeekDays = generateWeekDaysData(
        new Date(wmr.weekStartDate),
        new Date(wmr.weekEndDate),
      );

      wmr.plannedActualProgress.forEach((entry) => {
        const entryDate = new Date(entry.dayDate);
        const entryDateStr = safeFormat(entryDate, "yyyy-MM-dd");

        // Find matching day in weekDaysData
        const dayIndex = updatedWeekDays.findIndex(
          (day) => safeFormat(day.date, "yyyy-MM-dd") === entryDateStr,
        );

        if (dayIndex !== -1) {
          if (!updatedWeekDays[dayIndex].entries) {
            updatedWeekDays[dayIndex].entries = [];
          }

          updatedWeekDays[dayIndex].entries.push({
            contractor: entry.contractorVendorName || "",
            activity: entry.targetedActivityDetails || "",
            status: entry.status || "OPEN",
            variance: entry.varianceReason || "",
            expectedCompletionDate: entry.expectedCompletionDate
              ? new Date(entry.expectedCompletionDate)
              : null,
          });
        }
      });

      setWeekDaysData(updatedWeekDays);
    }

    // Manpower Deployment
    if (wmr.manpowerDeployment && wmr.manpowerDeployment.length > 0) {
      // Re-generate the 6 dates of the week for consistent mapping (dd/MM/yy)
      // Robust date generation to avoid timezone shifts
      const weekDates = [];
      let startDate;
      if (typeof wmr.weekStartDate === "string") {
        const [year, month, day] = wmr.weekStartDate
          .split("T")[0]
          .split("-")
          .map(Number);
        startDate = new Date(year, month - 1, day);
      } else {
        startDate = new Date(wmr.weekStartDate);
      }

      for (let i = 0; i < 6; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        weekDates.push(format(date, "dd/MM/yy"));
      }
      const displayDates = weekDates;
      console.log("Generated weekDates for mapping in ViewWMR:", weekDates);

      const laborGroups = {};
      wmr.manpowerDeployment.forEach((item) => {
        const labourType = (item.labourType || item.laborType || "").trim();
        if (!labourType) return;

        if (!laborGroups[labourType]) {
          laborGroups[labourType] = new Array(6).fill(0);
        }

        // Ensure dailyCount is a plain object
        let dailyCount = item.dailyCount;
        if (dailyCount && typeof dailyCount === "object") {
          if (dailyCount.$__parent) {
            dailyCount = Object.fromEntries(dailyCount);
          } else if (typeof dailyCount.toJSON === "function") {
            dailyCount = dailyCount.toJSON();
          }
        } else {
          dailyCount = {};
        }

        console.log(`Processing labour type: ${labourType}`, dailyCount);

        Object.entries(dailyCount).forEach(([date, count]) => {
          // Robust matching: normalize date string to dd/MM/yy
          const normalizeDate = (dStr) => {
            if (!dStr) return "";
            let s = dStr.toString().trim();

            // Handle ISO format YYYY-MM-DD...
            if (s.includes("-")) {
              const parts = s.split("T")[0].split("-");
              if (parts.length === 3) {
                const [y, m, d] = parts;
                return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y.slice(-2)}`;
              }
            }

            // Handle DD/MM/YY or DD/MM/YYYY
            if (s.includes("/")) {
              const parts = s.split("/");
              if (parts.length === 3) {
                const d = parts[0].padStart(2, "0");
                const m = parts[1].padStart(2, "0");
                let y = parts[2];
                if (y.length === 4) y = y.slice(-2);
                return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y.padStart(2, "0")}`;
              }
            }

            try {
              const parsed = new Date(s);
              if (!isNaN(parsed.getTime())) {
                return format(parsed, "dd/MM/yy");
              }
            } catch (e) {}

            return s;
          };

          const normalizedDate = normalizeDate(date);
          let dateIndex = weekDates.indexOf(normalizedDate);

          // Fallback: Digits-only matching
          if (dateIndex === -1) {
            const getDigits = (str) => str.replace(/\D/g, "").slice(-12); // Use last 6 digits for ddMMyy
            const searchDigits = normalizedDate.replace(/\D/g, "").slice(-6);
            dateIndex = weekDates.findIndex(
              (wd) => wd.replace(/\D/g, "").slice(-6) === searchDigits,
            );
          }

          if (dateIndex !== -1) {
            laborGroups[labourType][dateIndex] =
              (laborGroups[labourType][dateIndex] || 0) +
              (parseInt(count) || 0);
          } else {
            console.warn(
              `No match for date ${date} (normalized: ${normalizedDate}) in weekDates`,
              weekDates,
            );
          }
        });
      });

      const laborTypes = Object.entries(laborGroups).map(([type, data]) => ({
        type,
        data,
      }));

      setWmrData((prev) => ({
        ...prev,
        manpowerDeployment: {
          laborTypes,
          dates: weekDates,
        },
      }));
    } else {
      // Handle empty manpower deployment - show default structure
      const weekDates = [];
      const startDate = new Date(wmr.weekStartDate);
      for (let i = 0; i < 6; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        weekDates.push(format(date, "dd/MM/yy")); // Match database format
      }

      const defaultLaborTypes = [
        { type: "Engineer", data: [0, 0, 0, 0, 0, 0] },
        { type: "Supervisors", data: [0, 0, 0, 0, 0, 0] },
        { type: "Masons", data: [0, 0, 0, 0, 0, 0] },
        { type: "Helpers", data: [0, 0, 0, 0, 0, 0] },
      ];

      setWmrData((prev) => ({
        ...prev,
        manpowerDeployment: {
          laborTypes: defaultLaborTypes,
          dates: weekDates,
        },
      }));
    }

    // Machinery Equipment
    if (wmr.machineryEquipment && wmr.machineryEquipment.length > 0) {
      setWmrData((prev) => ({
        ...prev,
        machineryEquipment: wmr.machineryEquipment.map((m) => ({
          equipment: m.typeOfEquipment || "",
          quantity: m.quantity?.toString() || "0",
          hours: m.workingHours?.toString() || "0",
          utilization: m.utilizationStatus || "",
        })),
      }));
    }

    // Material Status
    if (wmr.materialStatus && wmr.materialStatus.length > 0) {
      setWmrData((prev) => ({
        ...prev,
        materialStatus: wmr.materialStatus.map((m) => ({
          description: m.materialDescription || "",
          received: m.materialReceived?.toString() || "0",
          consumed: m.materialConsumed?.toString() || "0",
          balance: m.balanceAtSite?.toString() || "0",
        })),
      }));
    }

    // Quality Control
    if (
      wmr.qualityControlActivities &&
      wmr.qualityControlActivities.length > 0
    ) {
      setWmrData((prev) => ({
        ...prev,
        qualityControl: wmr.qualityControlActivities.map((q) => ({
          inspection: q.inspectionCarriedOut || "",
          test: q.testConducted || "",
          observation: q.ncrObservation || "",
          status: q.status || "OPEN",
        })),
      }));
    }

    // Safety & HSE
    if (wmr.safetyHSEStatus) {
      setWmrData((prev) => ({
        ...prev,
        safetyHSE: {
          safetyCompliance: wmr.safetyHSEStatus.safetyCompliance || "",
          toolboxTalks: wmr.safetyHSEStatus.toolboxTalks || "",
          accidentsNearMisses: wmr.safetyHSEStatus.accidentsNearMisses || "",
        },
      }));
    }

    // Issues Constraints
    if (wmr.issuesConstraints) {
      setWmrData((prev) => ({
        ...prev,
        issuesConstraints: {
          technical: wmr.issuesConstraints.technical || "",
          material: wmr.issuesConstraints.material || "",
          manpower: wmr.issuesConstraints.manpower || "",
          designApproval: wmr.issuesConstraints.designApproval || "",
        },
      }));
    }

    // Instructions Decisions
    if (wmr.instructionsDecisions && Array.isArray(wmr.instructionsDecisions)) {
      setWmrData((prev) => ({
        ...prev,
        instructionsDecisions: wmr.instructionsDecisions.map((instruction) => ({
          date: instruction.date ? new Date(instruction.date) : new Date(),
          visitorName: instruction.visitorName || "",
          designation: instruction.designation || "",
          instruction: instruction.instructions || "",
          remark: instruction.remarks || "",
        })),
      }));
    }

    // Decision Discussion
    if (wmr.decisionRecords && wmr.decisionRecords.length > 0) {
      setWmrData((prev) => ({
        ...prev,
        decisionDiscussion: wmr.decisionRecords[0].record || "",
      }));
    }

    // Plan for Next Week
    if (wmr.planNextWeek && wmr.planNextWeek.length > 0) {
      setWmrData((prev) => ({
        ...prev,
        planNextWeek: {
          targetActivities: wmr.planNextWeek[0].targetActivity || "",
          resourcePlanning: wmr.planNextWeek[0].resourcePlanning || "",
        },
      }));
    }

    // Photos
    if (wmr.photographicProgress && wmr.photographicProgress.length > 0) {
      const photos = wmr.photographicProgress.map((photo) => ({
        photoPath: photo.photoPath,
        description: photo.description || "",
        shapes: photo.shapes || [],
        uploadedAt: photo.uploadedAt,
        name: photo.description || "Photo",
        imageUrl: `${url}${photo.photoPath.replace(/\\/g, "/")}`,
      }));
      setWmrData((prev) => ({ ...prev, photos }));
    }

    // Project Layout Image
    if (wmr.projectLayoutImage) {
      setProjectLayoutImage(wmr.projectLayoutImage);
      const imagePath = wmr.projectLayoutImage.filePath.replace(/\\/g, "/");
      const imageUrl = imagePath.startsWith("/")
        ? `${url}${imagePath}`
        : `${url}/${imagePath}`;
      setProjectLayoutImagePreview(imageUrl);
    }
  };

  // Handle download report
  const handleDownloadReport = async () => {
    try {
      setDownloading(true);

      if (!currentWMR) {
        showToast("error", "Error", "No WMR data available for download.");
        return;
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: "blob",
      };

      // Download the report
      const downloadResponse = await axios.get(
        `${url}/api/wmr/download/${currentWMR._id}/report`,
        config,
      );

      // Create blob and download
      const urlBlob = window.URL.createObjectURL(
        new Blob([downloadResponse.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = urlBlob;

      // Generate filename
      const dateStr = weekStartDate
        ? safeFormat(weekStartDate, "yyyy-MM-dd")
        : new Date().toISOString().split("T")[0];
      const projectName =
        project?.projectName?.replace(/[^a-zA-Z0-9]/g, "_") || "WMR";
      link.download = `WMR_${projectName}_${dateStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);

      showToast("success", "Success", "WMR report downloaded successfully!");
    } catch (err) {
      console.error("Error downloading WMR report:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to download WMR report. Please try again.";
      showToast("error", "Error", errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  // Calculate manpower totals and averages
  const calculateManpowerValues = () => {
    const laborTypes = wmrData.manpowerDeployment.laborTypes;
    const totals = {};
    const averages = {};
    const columnTotals = [0, 0, 0, 0, 0, 0];
    let grandTotal = 0;

    laborTypes.forEach((labor) => {
      const values = labor.data;
      const total = values.reduce(
        (sum, val) => sum + (parseFloat(val) || 0),
        0,
      );
      const average = values.length > 0 ? Math.round(total / values.length) : 0;

      totals[labor.type] = total;
      averages[labor.type] = average;
      grandTotal += total;

      values.forEach((val, index) => {
        columnTotals[index] += parseFloat(val) || 0;
      });
    });

    const columnAverages = columnTotals.map((total) =>
      laborTypes.length > 0 ? Math.round(total / laborTypes.length) : 0,
    );

    const grandAverage =
      laborTypes.length > 0
        ? Math.round(grandTotal / (laborTypes.length * 6))
        : 0;

    return {
      totals,
      averages,
      columnTotals,
      columnAverages,
      grandTotal,
      grandAverage,
    };
  };

  // Show toast function
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={2}>
        <Alert severity="warning">Project not found</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const manpowerValues = calculateManpowerValues();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={2} sx={{ fontFamily: FONT_FAMILY }}>
        {/* ================= HEADER ================= */}
        <Box mb={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H2,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Weekly Monitoring Report (WMR)
              </Typography>

              <Button
                onClick={handleDownloadReport}
                disabled={downloading}
                startIcon={
                  downloading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Icon name="download1" />
                  )
                }
                sx={{
                  backgroundColor: COLORS.SECONDARY,
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "7px",
                  px: 2,
                  height: 36,
                  "&:hover": {
                    backgroundColor: COLORS.PRIMARY,
                  },
                }}
              >
                {downloading ? "Downloading..." : "Download Report"}
              </Button>
            </Box>

            <IconButton size="small" onClick={() => navigate(-1)}>
              <Icon name="close3" color={COLORS.ERROR} />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={0.4}>
            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Project Name:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.projectName}
                </span>
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap={isMobile ? "wrap" : "nowrap"}
              >
                <Typography
                  sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
                >
                  Week:
                </Typography>

                {weekStartDate && weekEndDate && (
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                    }}
                  >
                    {formatDateForWeekHeader(weekStartDate)} -{" "}
                    {formatDateForWeekHeader(weekEndDate)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Address:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.siteAddress || "N/A"}
                </span>
              </Typography>

              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                PMC:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {companies.find((c) => c._id === selectedCompany)?.name ||
                    "N/A"}
                </span>
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Developer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.developerName || "N/A"}
                </span>
              </Typography>

              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Engineer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.assignedEngineer?.name ||
                    project.assignedEngineerName ||
                    "Not Assigned"}
                </span>
              </Typography>
            </Box>
          </Box>

          {/* HEADER BOTTOM LINE */}
          <Box
            mt={1.5}
            height="1px"
            sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
          />
        </Box>

        {/* ================= 1. STAKEHOLDERS ================= */}
        <Typography
          sx={{
            ...TYPOGRAPHY.H4,
            mb: 1,
            mt: 2,
            fontFamily: FONT_FAMILY,
            color: COLORS.TEXT,
          }}
        >
          Stakeholders
        </Typography>

        <Box
          display="flex"
          sx={{
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            py: 0.8,
            ...TYPOGRAPHY.H4,
            color: COLORS.TEXT,
            fontFamily: FONT_FAMILY,
          }}
        >
          <Box width="20%" px={1}>
            Role
          </Box>
          <Box width="30%" px={1}>
            Name
          </Box>
          <Box width="50%" px={1}>
            Description
          </Box>
        </Box>

        {project?.stakeholders?.map((stakeholder, i) => (
          <Box
            key={i}
            display="flex"
            sx={{ py: 1, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}
          >
            <Box
              width="20%"
              px={1}
              sx={{
                ...TYPOGRAPHY.BODY_LARGE,
                fontFamily: FONT_FAMILY,
                color: COLORS.SECONDARY,
              }}
            >
              {stakeholder.role || "N/A"}
            </Box>
            <Box
              width="30%"
              px={1}
              sx={{
                ...TYPOGRAPHY.BODY_LARGE,
                fontFamily: FONT_FAMILY,
                color: COLORS.SECONDARY,
              }}
            >
              {stakeholder.name || "N/A"}
            </Box>
            <Box
              width="50%"
              px={1}
              sx={{
                ...TYPOGRAPHY.BODY_LARGE,
                fontFamily: FONT_FAMILY,
                color: COLORS.SECONDARY,
              }}
            >
              {stakeholder.details || "N/A"}
            </Box>
          </Box>
        ))}

        {/* ================= PROJECT LAYOUT IMAGE ================= */}
        {projectLayoutImagePreview && (
          <>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 1,
                mt: 2,
                fontFamily: FONT_FAMILY,
                color: COLORS.TEXT,
              }}
            >
              Project Layout Image
            </Typography>

            <Box
              sx={{
                border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}
            >
              <Box
                component="img"
                src={projectLayoutImagePreview}
                alt="Project Layout"
                sx={{
                  width: "100%",
                  maxHeight: "300px",
                  objectFit: "contain",
                  borderRadius: 1,
                  mb: 1,
                }}
              />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.SECONDARY }}
                >
                  {projectLayoutImage?.fileName}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* ================= 2. WORK EXECUTED ================= */}
        <Typography
          sx={{
            ...TYPOGRAPHY.H3,
            color: COLORS.PRIMARY,
            mt: 4,
            mb: 1.5,
            fontFamily: FONT_FAMILY,
          }}
        >
          2. Work Executed During the Week:
        </Typography>

        {/* RCC Work */}
        <Box mb={2.5}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              mb: 0.8,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
            }}
          >
            RCC Work
          </Typography>
          <RichTextDisplay
            value={wmrData.workExecuted.rccWork}
            placeholder="No RCC work description available"
          />
        </Box>

        {/* Civil Works */}
        <Box mb={2.5}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              mb: 0.8,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
            }}
          >
            Civil Works
          </Typography>
          <RichTextDisplay
            value={wmrData.workExecuted.civilWorks}
            placeholder="No civil works description available"
          />
        </Box>

        {/* Masonry / Finishing Works */}
        <Box mb={2.5}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              mb: 0.8,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
            }}
          >
            Masonry / Finishing Works
          </Typography>
          <RichTextDisplay
            value={wmrData.workExecuted.masonryFinishing}
            placeholder="No masonry/finishing works description available"
          />
        </Box>

        {/* MEP Works */}
        <Box mb={2.5}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              mb: 0.8,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
            }}
          >
            MEP Works
          </Typography>
          <RichTextDisplay
            value={wmrData.workExecuted.mepWorks}
            placeholder="No MEP works description available"
          />
        </Box>

        {/* External Development */}
        <Box mb={2.5}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              mb: 0.8,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
            }}
          >
            External Development
          </Typography>
          <RichTextDisplay
            value={wmrData.workExecuted.externalDevelopment}
            placeholder="No external development work description available"
          />
        </Box>

        {/* ================= 3. WORK IN PROGRESS ================= */}
        <Typography
          sx={{
            ...TYPOGRAPHY.H3,
            color: COLORS.PRIMARY,
            mt: 4,
            mb: 1.5,
            fontFamily: FONT_FAMILY,
          }}
        >
          3. Work in Progress (WIP):
        </Typography>

        {/* Merged Work Progress Table */}
        {wmrData.workInProgress.mergedProgress.length > 0 ? (
          <Box mb={3}>
            {/* Table Header */}
            <Box
              display="flex"
              sx={{
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                py: 0.8,
              }}
            >
              <Box
                flex={1}
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Activity
              </Box>
              <Box
                flex={1}
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Location
              </Box>
              <Box
                flex={1}
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Status
              </Box>
              <Box
                flex={1}
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Remark
              </Box>
            </Box>

            {/* Table Body */}
            {wmrData.workInProgress.mergedProgress.map((row, i) => (
              <Box
                key={i}
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 0.8,
                }}
              >
                <Box
                  flex={1}
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                  }}
                >
                  {row.activity || "-"}
                </Box>
                <Box
                  flex={1}
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                  }}
                >
                  {row.location || "-"}
                </Box>
                <Box
                  flex={1}
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                  }}
                >
                  {row.status || "-"}
                </Box>
                <Box
                  flex={1}
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                  }}
                >
                  {row.remark || "-"}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            mb={3}
            sx={{
              textAlign: "center",
              py: 3,
              color: COLORS.MUTED_TEXT,
              fontStyle: "italic",
            }}
          >
            No work in progress data available
          </Box>
        )}

        {/* ================= 4. PLANNED VS ACTUAL PROGRESS ================= */}
        <Box mt={4}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              color: COLORS.PRIMARY,
              fontFamily: FONT_FAMILY,
              mb: 1.5,
            }}
          >
            4. Planned vs Actual Progress:
          </Typography>

          {/* Table Header */}
          <Box
            display="flex"
            sx={{
              py: 1.2,
              ...TYPOGRAPHY.H4,
              color: COLORS.TEXT,
              fontFamily: FONT_FAMILY,
              borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              alignItems: "center",
            }}
          >
            <FlexCell
              w="160px"
              borderBottom="none"
              sx={{ display: "flex", alignItems: "center" }}
            >
              Week Days
            </FlexCell>
            <FlexCell flex={1} sx={{ display: "flex", alignItems: "center" }}>
              Contractor/Vendor
            </FlexCell>
            <FlexCell flex={1.4} sx={{ display: "flex", alignItems: "center" }}>
              Targeted Activity Details
            </FlexCell>
            <FlexCell w="100px" sx={{ display: "flex", alignItems: "center" }}>
              Status
            </FlexCell>
            <FlexCell flex={1.2} sx={{ display: "flex", alignItems: "center" }}>
              Variance/Reason
            </FlexCell>
            <FlexCell w="140px" sx={{ display: "flex", alignItems: "center" }}>
              Expected Completion Date
            </FlexCell>
          </Box>

          {/* Table Body */}
          {weekDaysData.map((dayBlock, dayIndex) => (
            <Box key={dayIndex}>
              {dayBlock.entries.map((row, entryIndex) => (
                <FlexRow key={entryIndex}>
                  {/* WEEK COLUMN */}
                  <FlexCell
                    w="160px"
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {entryIndex === 0 && (
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          color: COLORS.SECONDARY,
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {dayBlock.day.split(" ")[0]}
                        <br />
                        {dayBlock.formattedDate.split(" ").slice(1).join(" ")}
                      </Typography>
                    )}
                  </FlexCell>

                  {/* DATA COLUMNS */}
                  <FlexRowInner>
                    <FlexCell
                      flex={1}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography
                        sx={{
                          color: COLORS.SECONDARY,
                          ...TYPOGRAPHY.BODY_LARGE,
                          wordWrap: "break-word",
                          lineHeight: 1.4,
                          whiteSpace: "normal",
                        }}
                      >
                        {row.contractor}
                      </Typography>
                    </FlexCell>
                    <FlexCell
                      flex={1.4}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography
                        sx={{
                          color: COLORS.SECONDARY,
                          ...TYPOGRAPHY.BODY_LARGE,
                          wordWrap: "break-word",
                          lineHeight: 1.4,
                          whiteSpace: "normal",
                        }}
                      >
                        {row.activity}
                      </Typography>
                    </FlexCell>
                    <FlexCell
                      w="100px"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography
                        sx={{
                          color: COLORS.SECONDARY,
                          ...TYPOGRAPHY.BODY_LARGE,
                          wordWrap: "break-word",
                          lineHeight: 1.4,
                          fontWeight: row.status === "OPEN" ? "bold" : "normal",
                          color:
                            row.status === "OPEN"
                              ? COLORS.PRIMARY
                              : COLORS.SECONDARY,
                        }}
                      >
                        {row.status || "OPEN"}
                      </Typography>
                    </FlexCell>
                    <FlexCell
                      flex={1.2}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography
                        sx={{
                          color: COLORS.SECONDARY,
                          ...TYPOGRAPHY.BODY_LARGE,
                          wordWrap: "break-word",
                          lineHeight: 1.4,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {row.variance || "-"}
                      </Typography>
                    </FlexCell>
                    <FlexCell
                      w="140px"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography
                        sx={{
                          color: COLORS.SECONDARY,
                          ...TYPOGRAPHY.BODY_LARGE,
                          wordWrap: "break-word",
                          lineHeight: 1.4,
                          whiteSpace: "normal",
                        }}
                      >
                        {row.expectedCompletionDate
                          ? safeFormat(row.expectedCompletionDate, "dd/MM/yyyy")
                          : "-"}
                      </Typography>
                    </FlexCell>
                  </FlexRowInner>
                </FlexRow>
              ))}

              {/* Show empty row with date when no entries */}
              {dayBlock.entries.length === 0 && (
                <FlexRow>
                  <FlexCell
                    w="160px"
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.SECONDARY,
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {dayBlock.day.split(" ")[0]}
                      <br />
                      {dayBlock.formattedDate.split(" ").slice(1).join(" ")}
                    </Typography>
                  </FlexCell>
                  <FlexRowInner>
                    <FlexCell
                      flex={1}
                      sx={{ display: "flex", alignItems: "center" }}
                    />
                    <FlexCell
                      flex={1.4}
                      sx={{ display: "flex", alignItems: "center" }}
                    />
                    <FlexCell
                      w="100px"
                      sx={{ display: "flex", alignItems: "center" }}
                    />
                    <FlexCell
                      flex={1.2}
                      sx={{ display: "flex", alignItems: "center" }}
                    />
                    <FlexCell
                      w="140px"
                      sx={{ display: "flex", alignItems: "center" }}
                    />
                  </FlexRowInner>
                </FlexRow>
              )}

              {/* DAY SEPARATOR */}
              <Box
                mt={2}
                height="1px"
                sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
              />
            </Box>
          ))}
        </Box>

        {/* ================= 5. MANPOWER DEPLOYMENT ================= */}
        <Box mt={5}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              color: COLORS.PRIMARY,
              fontFamily: FONT_FAMILY,
              mb: 1.5,
            }}
          >
            5. Manpower Deployment:
          </Typography>

          {wmrData.manpowerDeployment.laborTypes.length > 0 ? (
            <>
              {/* Header Row */}
              <Box
                display="flex"
                gap={0.5}
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  pb: 1,
                  mb: 2,
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <Box
                  width="180px"
                  pl={2}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.TEXT,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Labour Type
                </Box>
                {wmrData.manpowerDeployment.dates.map((date, i) => (
                  <Box
                    key={i}
                    width="90px"
                    textAlign="center"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.TEXT,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {date}
                  </Box>
                ))}
                <Box
                  width="90px"
                  textAlign="center"
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.TEXT,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Average
                </Box>
                <Box
                  width="90px"
                  textAlign="center"
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.TEXT,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Total
                </Box>
              </Box>

              {/* Data Rows */}
              {wmrData.manpowerDeployment.laborTypes.map((labor, typeIndex) => (
                <Box key={typeIndex} display="flex" gap={0.5} sx={{ mb: 2 }}>
                  <Box width="180px" sx={viewCellStyle}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                        pl: 2,
                      }}
                    >
                      {labor.type}
                    </Typography>
                  </Box>
                  {labor.data.map((value, dayIndex) => (
                    <Box key={dayIndex} width="90px" sx={viewCellStyle}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          color: COLORS.PRIMARY,
                          fontFamily: FONT_FAMILY,
                          textAlign: "center",
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    width="90px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.SECONDARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {manpowerValues.averages[labor.type] || 0}
                    </Typography>
                  </Box>
                  <Box
                    width="90px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.SECONDARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {manpowerValues.totals[labor.type] || 0}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {/* Total Row */}
              <Box
                mt={3}
                pt={2}
                display="flex"
                gap={0.5}
                sx={{ borderTop: `2px solid ${COLORS.LIGHT_SECONDARY}` }}
              >
                <Box
                  width="180px"
                  sx={{
                    ...viewCellStyle,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.PRIMARY,
                      fontFamily: FONT_FAMILY,
                      pl: 2,
                    }}
                  >
                    Total
                  </Typography>
                </Box>
                {manpowerValues.columnTotals.map((total, i) => (
                  <Box
                    key={i}
                    width="90px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.SECONDARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {total}
                    </Typography>
                  </Box>
                ))}
                <Box
                  width="90px"
                  sx={{
                    ...viewCellStyle,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      fontFamily: FONT_FAMILY,
                      textAlign: "center",
                    }}
                  >
                    {manpowerValues.grandAverage}
                  </Typography>
                </Box>
                <Box
                  width="90px"
                  sx={{
                    ...viewCellStyle,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      fontFamily: FONT_FAMILY,
                      textAlign: "center",
                    }}
                  >
                    {manpowerValues.grandTotal}
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                color: COLORS.MUTED_TEXT,
                fontStyle: "italic",
              }}
            >
              No manpower deployment data available
            </Box>
          )}
        </Box>

        {/* ================= 6. MACHINERY & EQUIPMENTS ================= */}
        <Box mt={5}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              color: COLORS.PRIMARY,
              fontFamily: FONT_FAMILY,
              mb: 1.5,
            }}
          >
            6. Machinery & Equipments:
          </Typography>

          {wmrData.machineryEquipment.length > 0 ? (
            <>
              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  pb: 1,
                  mb: 2,
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                <Box flex={2}>Type of Equipment</Box>
                <Box width="120px" textAlign="center">
                  Quantity
                </Box>
                <Box width="140px" textAlign="center">
                  Working Hrs
                </Box>
                <Box flex={1.2}>Purpose</Box>
              </Box>

              {/* Table Rows */}
              {wmrData.machineryEquipment.map((machine, index) => (
                <Box
                  key={index}
                  display="flex"
                  gap={2}
                  mb={2}
                  alignItems="center"
                >
                  <Box flex={2} sx={viewCellStyle}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {machine.equipment || "-"}
                    </Typography>
                  </Box>
                  <Box
                    width="120px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {machine.quantity || "0"}
                    </Typography>
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {machine.hours || "0"}
                    </Typography>
                  </Box>
                  <Box flex={1.2} sx={viewCellStyle}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {machine.utilization || "-"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                color: COLORS.MUTED_TEXT,
                fontStyle: "italic",
              }}
            >
              No machinery & equipment data available
            </Box>
          )}
        </Box>

        {/* ================= 7. MATERIAL STATUS ================= */}
        <Box mt={4}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              color: COLORS.PRIMARY,
              mb: 1.5,
              fontFamily: FONT_FAMILY,
            }}
          >
            7. Material Status:
          </Typography>

          {wmrData.materialStatus.length > 0 ? (
            <>
              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 1,
                  ...TYPOGRAPHY.H4,
                  color: COLORS.TEXT,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                <Box flex={2}>Material Description</Box>
                <Box width="140px" textAlign="center">
                  Material Received
                </Box>
                <Box width="140px" textAlign="center">
                  Material Consumed
                </Box>
                <Box width="140px" textAlign="center">
                  Balance At Site
                </Box>
              </Box>

              {/* Table Rows */}
              {wmrData.materialStatus.map((material, index) => (
                <Box
                  key={index}
                  display="flex"
                  gap={2}
                  mb={2}
                  alignItems="center"
                >
                  <Box flex={2} sx={viewCellStyle}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {material.description || "-"}
                    </Typography>
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {material.received || "0"}
                    </Typography>
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {material.consumed || "0"}
                    </Typography>
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {material.balance || "0"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                color: COLORS.MUTED_TEXT,
                fontStyle: "italic",
              }}
            >
              No material status data available
            </Box>
          )}
        </Box>

        {/* ================= 8. QUALITY CONTROL ACTIVITIES ================= */}
        <Box mt={4}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              color: COLORS.PRIMARY,
              mb: 1.5,
              fontFamily: FONT_FAMILY,
            }}
          >
            8. Quality Control Activities:
          </Typography>

          {wmrData.qualityControl.length > 0 ? (
            <>
              {/* Table Header */}
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 1,
                  mb: 2,
                  ...TYPOGRAPHY.H4,
                  color: COLORS.TEXT,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <Box flex={2.2} pl={2}>
                  Inspection Carried Out
                </Box>
                <Box width="140px" textAlign="center">
                  Test Conducted
                </Box>
                <Box flex={1.8} pl={2}>
                  NCR / Observations
                </Box>
                <Box width="120px" textAlign="center">
                  Status
                </Box>
              </Box>

              {/* Table Rows */}
              {wmrData.qualityControl.map((row, index) => (
                <Box
                  key={index}
                  display="flex"
                  gap={2}
                  mb={2}
                  alignItems="center"
                >
                  <Box flex={2.2} sx={viewCellStyle}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {row.inspection || "-"}
                    </Typography>
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {row.test || "0"}
                    </Typography>
                  </Box>
                  <Box flex={1.8} sx={viewCellStyle}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {row.observation || "-"}
                    </Typography>
                  </Box>
                  <Box
                    width="120px"
                    sx={{
                      ...viewCellStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color:
                          row.status === "OPEN"
                            ? COLORS.PRIMARY
                            : COLORS.SECONDARY,
                        fontFamily: FONT_FAMILY,
                        fontWeight: row.status === "OPEN" ? "bold" : "normal",
                        textAlign: "center",
                      }}
                    >
                      {row.status || "OPEN"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                color: COLORS.MUTED_TEXT,
                fontStyle: "italic",
              }}
            >
              No quality control activities data available
            </Box>
          )}
        </Box>

        {/* ================= 9. SAFETY & HSE STATUS ================= */}
        <Box mt={4}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
              mb: 2,
            }}
          >
            9. Safety & HSE Status:
          </Typography>

          {/* Safety Compliance */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Safety Compliance
            </Typography>
            <RichTextDisplay
              value={wmrData.safetyHSE.safetyCompliance}
              placeholder="No safety compliance data available"
            />
          </Box>

          {/* Toolbox Talks */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Toolbox Talks
            </Typography>
            <RichTextDisplay
              value={wmrData.safetyHSE.toolboxTalks}
              placeholder="No toolbox talks data available"
            />
          </Box>

          {/* Accidents / Near Misses */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Accidents / Near Misses
            </Typography>
            <RichTextDisplay
              value={wmrData.safetyHSE.accidentsNearMisses}
              placeholder="No accidents/near misses data available"
            />
          </Box>
        </Box>

        {/* ================= 10. ISSUES / CONSTRAINTS ================= */}
        <Box mt={4}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
              mb: 2,
            }}
          >
            10. Issues / Constraints:
          </Typography>

          {/* Technical */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Technical
            </Typography>
            <RichTextDisplay
              value={wmrData.issuesConstraints.technical}
              placeholder="No technical issues/constraints data available"
            />
          </Box>

          {/* Material */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Material
            </Typography>
            <RichTextDisplay
              value={wmrData.issuesConstraints.material}
              placeholder="No material issues/constraints data available"
            />
          </Box>

          {/* Manpower */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Manpower
            </Typography>
            <RichTextDisplay
              value={wmrData.issuesConstraints.manpower}
              placeholder="No manpower issues/constraints data available"
            />
          </Box>

          {/* Design/Approval */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Design/Approval
            </Typography>
            <RichTextDisplay
              value={wmrData.issuesConstraints.designApproval}
              placeholder="No design/approval issues/constraints data available"
            />
          </Box>
        </Box>

        {/* ================= 11. INSTRUCTIONS / DECISIONS FROM VISITOR ================= */}
        <Typography
          sx={{
            ...TYPOGRAPHY.H3,
            color: COLORS.PRIMARY,
            mt: 4,
            mb: 1.5,
            fontFamily: FONT_FAMILY,
          }}
        >
          11. Instructions / Decisions from Visitor:
        </Typography>

        {/* Visitor Instructions Table */}
        {wmrData.instructionsDecisions.length > 0 ? (
          <Box mb={3}>
            {/* Table Header */}
            <Box
              display="flex"
              sx={{
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                py: 0.8,
              }}
            >
              <Box
                width="120px"
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Date
              </Box>
              <Box
                flex={1.5}
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Visitor Name
              </Box>
              <Box
                flex={1.2}
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Designation
              </Box>
              <Box
                flex={2}
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Instruction / Decision
              </Box>
              <Box
                flex={1.5}
                sx={{
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                Remark
              </Box>
            </Box>

            {/* Table Body */}
            {wmrData.instructionsDecisions.map((row, i) => (
              <Box
                key={i}
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 0.8,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  width="120px"
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", wordWrap: "break-word" }}>
                    {row.date ? safeFormat(row.date, "dd/MM/yyyy") : ""}
                  </Typography>
                </Box>
                <Box
                  flex={1.5}
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      wordWrap: "break-word",
                      lineHeight: 1.4,
                    }}
                  >
                    {row.visitorName || "-"}
                  </Typography>
                </Box>
                <Box
                  flex={1.2}
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      wordWrap: "break-word",
                      lineHeight: 1.4,
                    }}
                  >
                    {row.designation || "-"}
                  </Typography>
                </Box>
                <Box
                  flex={2}
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      wordWrap: "break-word",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {row.instruction || "-"}
                  </Typography>
                </Box>
                <Box
                  flex={1.5}
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.SECONDARY,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      wordWrap: "break-word",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {row.remark || "-"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            mb={3}
            sx={{
              textAlign: "center",
              py: 3,
              color: COLORS.MUTED_TEXT,
              fontStyle: "italic",
            }}
          >
            No visitor instructions/decisions data available
          </Box>
        )}

        {/* ================= 12. DECISION & DISCUSSION RECORDS ================= */}
        <Box mt={4}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
              mb: 2,
            }}
          >
            12. Decision & Discussion Records:
          </Typography>
          <RichTextDisplay
            value={wmrData.decisionDiscussion}
            placeholder="No decision & discussion records available"
            height="161px"
          />
        </Box>

        {/* ================= 13. PLAN FOR NEXT WEEK ================= */}
        <Box mt={4}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
              mb: 2,
            }}
          >
            13. Plan for Next Week
          </Typography>

          {/* Target Activities */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Target Activities
            </Typography>
            <RichTextDisplay
              value={wmrData.planNextWeek.targetActivities}
              placeholder="No target activities data available"
            />
          </Box>

          {/* Resource Planning */}
          <Box mb={2.5}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 0.8,
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
              }}
            >
              Resource Planning
            </Typography>
            <RichTextDisplay
              value={wmrData.planNextWeek.resourcePlanning}
              placeholder="No resource planning data available"
            />
          </Box>
        </Box>

        {/* ================= 14. PHOTOGRAPHIC PROGRESS ================= */}
        <Box mt={4}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
              mb: 2,
            }}
          >
            14. Photographic Progress
          </Typography>

          {wmrData.photos.length > 0 ? (
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
              {wmrData.photos.map((photo, index) => (
                <Box key={index} sx={{ position: "relative" }}>
                  {/* Photo Display */}
                  <img
                    src={photo.imageUrl}
                    alt="progress"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "300px",
                      objectFit: "contain",
                    }}
                  />

                  {/* Photo Info */}
                  <Box sx={{ p: 1 }}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_SMALL,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "medium",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {photo.description || photo.name}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                borderRadius: "12px",
                backgroundColor: "#fafafa",
              }}
            >
              <Icon name="image" size={48} color={COLORS.LIGHT_SECONDARY} />
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.LIGHT_SECONDARY,
                  mt: 2,
                  fontFamily: FONT_FAMILY,
                }}
              >
                No photos available
              </Typography>
            </Box>
          )}
        </Box>

        {/* Toast Component */}
        <AppToast
          open={toast.open}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={closeToast}
        />
      </Box>
    </LocalizationProvider>
  );
}

/* ================= STYLES ================= */

const viewCellStyle = {
  height: "44px",
  borderRadius: "8px",
  border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
  display: "flex",
  alignItems: "center",
  fontFamily: FONT_FAMILY,
  color: COLORS.SECONDARY,
  paddingLeft: "12px",
};
